import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import { Grid, IconButton, Stack, Tooltip } from '@mui/material';

import { VendorApi } from '@/api';
import { IcDeleteTrash } from '@/assets/images';
import { AppModal, FormCard, FormRoot, RenderFormField } from '@/components';
import { PhoneNumbersForm } from '@/components/CustomForms/PhoneNumbersForm';
import { Button } from '@/components/TailwindComponents';
import { phoneNumberInput } from '@/constants/form_inputs';
import {
  IVendor,
  IVendorContact,
  IVendorForm,
  IVendorType,
} from '@/models/vendors';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { parsePhoneNumber } from '@/utils';

const VENDOR_COURTS_DISABLED_MESSAGE =
  'Please enable vendors. This is required for both Return Court and Arraignment Court fields to display values.';

interface NewCourtVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newVendor: IVendor) => void;
}

const CourtVendorFormFields = [
  {
    name: 'name',
    type: 'text',
    label: 'Resource name',
    rules: {
      required: 'This field is required.',
    },
  },
  {
    ...phoneNumberInput,
    name: 'phoneNumber',
    label: 'Phone number',
    rules: {
      ...phoneNumberInput.rules,
      required: 'This field is required.',
    },
  },
  {
    name: 'email',
    type: 'email',
    label: 'Email address',
  },
  {
    name: 'address',
    type: 'address',
    label: 'Address',
    grid: {
      md: 8,
      sm: 12,
    },
  },
  {
    name: 'isActive',
    type: 'checkbox',
    label: 'Active',
    grid: {
      md: 4,
      sm: 12,
    },
  },
];

const NewCourtVendorModal = ({
  isOpen,
  onClose,
  onSuccess,
}: NewCourtVendorModalProps) => {
  const { updateToast } = useToastStore();
  const { account } = useUserStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courtType, setCourtType] = useState<IVendorType | null>(null);
  const isVendorsEnabled = account?.settings?.features?.isEnabledVendors;

  const ensureVendorsEnabledForCourts = () => {
    if (isVendorsEnabled) {
      return true;
    }

    updateToast({
      type: ConfirmTypes.WARNING,
      open: true,
      message: VENDOR_COURTS_DISABLED_MESSAGE,
    });

    return false;
  };

  const methods = useForm<IVendorForm>({
    mode: 'all',
    defaultValues: {
      isActive: true,
      type: 'Court',
    },
  });

  const { control, handleSubmit, reset } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  });

  // Fetch court vendor type on mount
  useEffect(() => {
    const fetchCourtType = async () => {
      if (!isVendorsEnabled) {
        updateToast({
          type: ConfirmTypes.WARNING,
          open: true,
          message: VENDOR_COURTS_DISABLED_MESSAGE,
        });
        setCourtType(null);
        return;
      }

      try {
        const res = await VendorApi.getTypes();
        const court = res.data.find(
          (vt: IVendorType) => vt.name.toLowerCase() === 'court',
        );
        if (court) {
          setCourtType(court);
        }
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    };

    if (isOpen) {
      fetchCourtType();
    }
  }, [isOpen, isVendorsEnabled, updateToast]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        isActive: true,
        type: 'Court',
      });
    }
  }, [isOpen, reset]);

  const onSubmit = async (values: IVendorForm) => {
    if (!ensureVendorsEnabledForCourts()) {
      return;
    }

    if (!courtType) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: 'Court vendor type not found. Please try again.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const parsedContacts = [
        { ...values.primaryContact, isPrimary: true },
        ...(values.contacts || []).map((contact: IVendorContact) => ({
          ...contact,
          isPrimary: false,
        })),
      ].map(({ name, phoneNumbers, isPrimary }) => ({
        name,
        isPrimary,
        phoneNumbers: (phoneNumbers || []).map(
          ({ phoneNumber, phoneNumberExt }) => ({
            phoneNumber: parsePhoneNumber(phoneNumber),
            phoneNumberExt,
          }),
        ),
      }));

      const data = {
        ...values,
        type: courtType.name,
        phoneNumber: parsePhoneNumber(values.phoneNumber),
        address: values.address?._id,
        contacts: parsedContacts,
      };

      const response = await VendorApi.save(data);

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Court vendor created successfully',
      });

      onSuccess(response.data);
    } catch (err: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: err.message || 'Failed to create court vendor',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset({
      isActive: true,
      type: 'Court',
    });
    onClose();
  };

  return (
    <AppModal open={isOpen} title="Add New Court" onClose={handleCancel}>
      <FormProvider {...methods}>
        <FormRoot onSubmit={handleSubmit(onSubmit)}>
          <FormCard title="Vendor information">
            <Grid xs={12} sm={6} md={3} item>
              <Stack spacing={0.5}>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Resource type
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 py-2 px-3 bg-gray-100 dark:bg-gray-700 rounded-md">
                  Court
                </span>
              </Stack>
            </Grid>
            {CourtVendorFormFields.map(({ grid, name, ...rest }: any) => (
              <Grid key={name} xs={12} sm={6} md={3} {...grid} item>
                <RenderFormField {...rest} name={name} />
              </Grid>
            ))}
          </FormCard>

          <FormCard title="Primary contact">
            <Stack width="100%" pl={2}>
              <RenderFormField
                name="primaryContact.name"
                label="Name"
                rules={{ required: 'This field is required.' }}
              />
            </Stack>
            <Stack width="100%" pl={2}>
              <PhoneNumbersForm<IVendorForm>
                name="primaryContact.phoneNumbers"
                control={control}
              />
            </Stack>
          </FormCard>

          <FormCard title="Additional contacts">
            {fields.map((item, index) => {
              return (
                <Stack key={item.id} width="100%" pl={2} my={1}>
                  <Stack flexDirection="row" maxWidth="500px">
                    <RenderFormField
                      name={`contacts.${index}.name`}
                      label="Name"
                    />
                    <Tooltip
                      title="DELETE this additional contact and all of the phone numbers"
                      placement="right-start"
                    >
                      <IconButton
                        onClick={() => remove(index)}
                        sx={{
                          color: colors.error.main,
                          mt: 2,
                          alignSelf: 'center',
                        }}
                      >
                        <IcDeleteTrash />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <PhoneNumbersForm<IVendorForm>
                    control={control}
                    name={`contacts.${index}.phoneNumbers`}
                  />
                </Stack>
              );
            })}

            <Button
              color="error"
              type="button"
              size="sm"
              variant="secondary"
              className="w-fit text-sm m-2"
              onClick={() => {
                append({ name: '', phoneNumbers: [], isPrimary: false });
              }}
            >
              + Add additional contact
            </Button>
          </FormCard>

          <Stack
            direction="row"
            spacing={2}
            width="100%"
            justifyContent="flex-end"
            alignItems="end"
            sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}
          >
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" color="error" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Save'}
            </Button>
          </Stack>
        </FormRoot>
      </FormProvider>
    </AppModal>
  );
};

export default NewCourtVendorModal;
